import { useEffect, useState } from "react";

import api from "@helpers/api";

// Query param that deep-links to one post of a community (`/:slug?post=<id>`).
// The community page reads it and opens the post modal; the feed stamps it
// when a post is opened, so the URL is the single source of truth for
// "which post is open".
export const POST_PARAM = "post";

// Backend cap on a comment body (CreateCommentFormRequest).
export const COMMENT_MAX_LENGTH = 1024;

// `api` resolves with the axios error instead of throwing, so a response is
// only good when it carries a 2xx status.
function ok(res) {
    return !!res && !res.isAxiosError && res.status >= 200 && res.status < 300;
}

export async function fetchPost(postId) {
    const res = await api.get(`community/group/post/${postId}`);
    return ok(res) && res.data?.id ? res.data : null;
}

/** @returns {{ data: object[], total: number, page: number, lastPage: number } | null} */
export async function fetchComments(postId, page = 1, perPage = 20) {
    const res = await api.get(
        `community/group/post/${postId}/comments/list?page=${page}&per_page=${perPage}`
    );
    if (!ok(res)) return null;

    return {
        data: Array.isArray(res.data?.data) ? res.data.data : [],
        total: Number(res.data?.total || 0),
        page: Number(res.data?.current_page || page),
        lastPage: Number(res.data?.last_page || 1),
    };
}

export async function reactToPost(postId, reaction = "heart") {
    const res = await api.post(`community/group/post/${postId}/reaction`, {
        type: "REACTION",
        payload: { reaction },
    });
    return ok(res) && !!res.data?.success;
}

/** @returns {{ comment_id: string } | null} */
export async function commentPost(postId, text) {
    const res = await api.post(`community/group/post/${postId}/comment`, { text });
    return ok(res) && res.data?.success ? res.data : null;
}

export function postShareLink(slug, postId) {
    return `${window.location.origin}/${slug}?${POST_PARAM}=${encodeURIComponent(postId)}`;
}

// Slug of the community a post belongs to: what the post itself carries,
// else the user's community list, else (a community page) the open `/:slug`.
export function communitySlugFor(post, lookup) {
    return (
        post?.community?.slug ||
        lookup?.(post?.community?.id)?.slug ||
        window.location.pathname.split("/").filter(Boolean)[0] ||
        ""
    );
}

/**
 * Hearts a post optimistically through the store and persists it. There is
 * no "unreact" endpoint, so an already-hearted post is left as is.
 * @returns {Promise<boolean>} false when the API refused it (the store is reverted)
 */
export async function heartPost(post) {
    if (!post?.id) return false;
    if (post.reactions?.heart?.reacted) return true;

    postStore.set(withHeart(post));
    const done = await reactToPost(post.id);
    if (!done) postStore.set(post);
    return done;
}

// A post with the viewer's heart added, for optimistic updates.
export function withHeart(post) {
    const heart = post.reactions?.heart || { amount: 0, reacted: false };
    return {
        ...post,
        reactions: {
            ...post.reactions,
            heart: { amount: Number(heart.amount || 0) + 1, reacted: true },
        },
        insights: {
            ...post.insights,
            reactions: Number(post.insights?.reactions || 0) + 1,
        },
    };
}

export function withCommentCount(post, delta) {
    return {
        ...post,
        insights: {
            ...post.insights,
            comments: Math.max(0, Number(post.insights?.comments || 0) + delta),
        },
    };
}

// ── Post store ─────────────────────────────────────────────────────────────
// One in-memory copy per post id shared by the feed cards and the post modal,
// so a reaction or a comment made in one place shows up in the other. Also
// lets the modal open instantly on a post the feed already holds.
const cache = new Map();
const listeners = new Set();

export const postStore = {
    get(id) {
        return id ? cache.get(id) : undefined;
    },
    set(post) {
        if (!post?.id) return;
        cache.set(post.id, post);
        listeners.forEach((cb) => cb(post));
    },
    remove(id) {
        cache.delete(id);
        listeners.forEach((cb) => cb({ id, removed: true }));
    },
    subscribe(cb) {
        listeners.add(cb);
        return () => listeners.delete(cb);
    },
};

/**
 * Keeps a component's copy of a post in sync with the store: returns the
 * freshest version known for `initial.id` (the store's, when it has one) and
 * re-renders whenever that post is updated elsewhere.
 */
export function usePostSync(initial) {
    const [post, setPost] = useState(() => postStore.get(initial?.id) || initial);

    useEffect(() => {
        setPost(postStore.get(initial?.id) || initial);
    }, [initial]);

    useEffect(() => {
        if (!initial?.id) return undefined;
        return postStore.subscribe((updated) => {
            if (updated.id !== initial.id) return;
            setPost(updated.removed ? null : updated);
        });
    }, [initial?.id]);

    return post;
}
